<div class="ask-ryn-container d-flex">
  <!-- Thread List (left panel) -->
  <div class="thread-list">
    <!-- Guided walkthroughs: published tours for this app, launched via the
         in-app-help wren. "On this page" first, then the rest of the app.
         Each section hides when empty. -->
    @if (walkthroughSections().onPage.length > 0) {
      <div class="walkthrough-list">
        <div class="walkthrough-list-header px-3 py-2">
          <strong class="small">On this page</strong>
        </div>
        @for (w of walkthroughSections().onPage; track w.id) {
          <button
            type="button"
            class="walkthrough-item d-flex align-items-center px-3 py-2"
            (click)="launchWalkthrough(w)"
            [title]="w.title"
          >
            <i class="fa fa-route me-2 text-muted"></i>
            <span class="small text-truncate">{{ w.title }}</span>
          </button>
        }
      </div>
    }

    @if (walkthroughSections().others.length > 0) {
      <div class="walkthrough-list">
        <div class="walkthrough-list-header px-3 py-2">
          <strong class="small">More in this app</strong>
        </div>
        @for (w of walkthroughSections().others; track w.id) {
          <button
            type="button"
            class="walkthrough-item d-flex align-items-center px-3 py-2"
            (click)="launchWalkthrough(w)"
            [title]="w.title"
          >
            <i class="fa fa-route me-2 text-muted"></i>
            <span class="small text-truncate">{{ w.title }}</span>
          </button>
        }
      </div>
    }

    <div class="thread-list-header d-flex align-items-center px-3 py-2">
      <strong class="small">Conversations</strong>
      <button
        kendoButton
        [themeColor]="'primary'"
        [size]="'small'"
        class="ms-auto"
        (click)="createNewConversation()"
      >
        <i class="fa fa-plus"></i>
      </button>
    </div>

    <div class="thread-list-items">
      @if (conversations().length === 0) {
        <div class="text-muted small px-3 py-2">
          No conversations yet. Click + to start.
        </div>
      }
      @for (conv of conversations(); track conv.Id) {
        <div
          class="thread-item px-3 py-2"
          [class.active]="activeConversation()?.Id === conv.Id"
          (click)="selectConversation(conv)"
        >
          @if (editingConversationId() === conv.Id) {
            <!-- Inline rename: Enter saves, Escape cancels. Click events stop
                 here so editing never re-selects the conversation. -->
            <div
              class="thread-title-edit d-flex align-items-center"
              (click)="$event.stopPropagation()"
            >
              <input
                type="text"
                class="form-control form-control-sm"
                [attr.maxlength]="MAX_TITLE_LENGTH"
                [value]="editingTitleDraft()"
                (input)="editingTitleDraft.set($any($event.target).value)"
                (keyup.enter)="saveRename($event)"
                (keyup.escape)="cancelRename($event)"
                placeholder="Conversation title"
                help-anchor="ask-ryn__rename-title-input"
              />
              <button
                type="button"
                class="btn btn-sm rename-action"
                title="Save title"
                (click)="saveRename($event)"
                help-anchor="ask-ryn__rename-save"
              >
                <i class="fa fa-check"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm rename-action"
                title="Cancel"
                (click)="cancelRename($event)"
                help-anchor="ask-ryn__rename-cancel"
              >
                <i class="fa fa-xmark"></i>
              </button>
            </div>
          } @else {
            <div class="d-flex align-items-center">
              <div class="thread-title small text-truncate flex-grow-1">
                {{ conv.Title }}
              </div>
              <button
                type="button"
                class="btn btn-sm rename-action thread-rename-btn"
                title="Rename conversation"
                (click)="startRename(conv, $event)"
                help-anchor="ask-ryn__rename-conversation"
              >
                <i class="fa fa-pencil"></i>
              </button>
            </div>
            <div class="thread-time text-muted" style="font-size: 0.7rem">
              {{ conv.UpdatedAt | askRynRelativeTime: now() }}
            </div>
          }
        </div>
      }
    </div>
  </div>

  <!-- Chat Panel (right) -->
  <div class="chat-panel flex-grow-1 d-flex flex-column">
    @if (errorMsg()) {
      <div class="ask-ryn-error small text-danger px-3 py-2">
        <i class="fa fa-triangle-exclamation me-1"></i>
        {{ errorMsg() }}
      </div>
    }
    @if (askRynUi.gridNotice(); as notice) {
      <div class="ask-ryn-error small text-warning px-3 py-2">{{ notice }}</div>
    }
    @if (!activeConversation()) {
      <div
        class="empty-state d-flex flex-column align-items-center justify-content-center flex-grow-1"
      >
        <i class="fa fa-comments fa-3x text-muted mb-3"></i>
        <p class="text-muted">Select a conversation or start a new one</p>
        <button
          kendoButton
          [themeColor]="'primary'"
          (click)="createNewConversation()"
        >
          <i class="fa fa-plus me-1"></i>
          New Conversation
        </button>
      </div>
    } @else {
      @if (isLoading()) {
        <div class="d-flex justify-content-center p-4">
          <kendo-loader
            [type]="'converging-spinner'"
            [size]="'medium'"
          ></kendo-loader>
        </div>
      }

      <kendo-chat
        [messages]="displayFeed()"
        [authorId]="chatUser.id"
        [messageWidthMode]="'full'"
        (sendMessage)="sendMessage($event)"
        class="flex-grow-1"
        [attr.placeholder]="'Ask Ryn anything about this page...'"
      >
        <!-- Render Ryn's (receiver) replies as Markdown via the shared
             ngx-markdown visualization (same GFM dialect as the docs and
             knowledge-base). The user's own sent messages keep Kendo's
             default plain-text rendering, so only Ryn's answers are
             formatted (bold, lists, code, tables, links).
             The transient typing bubble (sentinel text) renders the animated
             "Ryn is thinking" spinner IN the stream, where the reply lands. -->
        <ng-template kendoChatReceiverMessageContentTemplate let-message>
          @if (message.text === TYPING_SENTINEL) {
            <span class="typing-indicator text-muted">
              <i class="fa fa-circle-notch fa-spin me-1"></i>
              Ryn is thinking...
            </span>
          } @else {
            <!-- A reply may carry prose AND inline ```chart blocks. Parse once
                 (memoized), render the prose as Markdown and each chart as a
                 Kendo chart below it. -->
            @let parsed = parseAssistantContent(message.text);
            @if (parsed.markdown) {
              <markdown class="ask-ryn-md" [data]="parsed.markdown"></markdown>
            }
            @for (chart of parsed.charts; track $index) {
              <kendo-chart class="ask-ryn-chart">
                @if (chart.spec.title) {
                  <kendo-chart-title
                    [text]="chart.spec.title"
                  ></kendo-chart-title>
                }
                <kendo-chart-legend
                  [visible]="
                    chart.spec.type === 'pie' || chart.spec.series.length > 1
                  "
                  position="bottom"
                ></kendo-chart-legend>
                @if (chart.spec.type !== 'pie') {
                  <kendo-chart-category-axis>
                    <kendo-chart-category-axis-item
                      [categories]="chart.spec.categories"
                    ></kendo-chart-category-axis-item>
                  </kendo-chart-category-axis>
                }
                <kendo-chart-series>
                  @if (chart.spec.type === 'pie') {
                    <kendo-chart-series-item
                      type="pie"
                      [data]="chart.pieData"
                      categoryField="category"
                      field="value"
                    ></kendo-chart-series-item>
                  } @else {
                    @for (s of chart.spec.series; track $index) {
                      <kendo-chart-series-item
                        [type]="chart.spec.type"
                        [data]="s.data"
                        [name]="s.name"
                      ></kendo-chart-series-item>
                    }
                  }
                </kendo-chart-series>
              </kendo-chart>
            }
            <!-- WI 37362: a ```workbench block shows as SQL in the prose; the host's link opens it in Ryn
                 Workbench (never run). The href carries the query, so it stays out of Ryn Replay capture. -->
            @if (workbenchShare; as share) {
              @for (link of parsed.workbenchLinks; track $index) {
                <a
                  class="ask-ryn-workbench-link ryn-replay-block"
                  [href]="link.href"
                  [title]="share.title"
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerpolicy="no-referrer"
                >
                  <i class="fa fa-database me-1"></i>
                  {{ share.label }}
                  <span class="ask-ryn-workbench-where">
                    ({{ link.where }})
                  </span>
                </a>
              }
            }
          }
        </ng-template>
      </kendo-chat>

      <!-- Second typing indicator pinned below the composer. Kept alongside the
           in-stream bubble per Tim's request (both parts). -->
      @if (isSending() || isAwaitingReply()) {
        <div class="typing-indicator px-3 py-1">
          <small class="text-muted">
            <i class="fa fa-circle-notch fa-spin me-1"></i>
            Ryn is thinking...
          </small>
        </div>
      }
    }
  </div>
</div>
